<template>
  <ComponentDrawer
    v-if="componentEditMode && componentEditMode.type === ComponentTypes.Root"
    :base-stacking-context="props.baseStackingContext"
    :componentType="type"
    :extended-title="t('canvasUI.canvasBuilder.components.edit')"
    :is-close-disabled="!!drawerComponentRef?.isBusy"
    @update:show="onUpdateShow"
  >
    <RootSettings id="root" ref="drawerComponentRef" :data="activeCanvas" />
  </ComponentDrawer>
  <Component
    :is="modalComponentsMap[type]"
    v-else-if="isModalComponent"
    :id="activeEditNode?.id"
    :data="computedActiveEditNode?.data ?? null"
    :theme_meta="activeEditNode?.theme_meta"
  />
  <ComponentDrawer
    v-else-if="isDrawerComponent"
    :base-stacking-context="props.baseStackingContext"
    :componentType="type"
    :extended-title="t('canvasUI.canvasBuilder.components.edit')"
    :is-close-disabled="!!drawerComponentRef?.isBusy"
    @update:show="onUpdateShow"
  >
    <Component
      :is="drawerComponentsMap[type]"
      :id="activeEditNode?.id"
      ref="drawerComponentRef"
      :data="computedActiveEditNode?.data"
      :used-in-section-id="
        componentEditMode && componentEditMode.usedInSectionId ? componentEditMode.usedInSectionId : undefined
      "
    />
  </ComponentDrawer>
</template>

<script setup lang="ts">
import { computed, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import ComponentDrawer from '@canvas-builder/components/container/ComponentDrawer/ComponentDrawer.vue'
import CarouselSettings from '@canvas-builder/components/ui/Carousel/Carousel.settings.vue'
import DataChartsSettings from '@canvas-builder/components/ui/DataCharts/DataCharts.settings.vue'
import DynamicDataTableSettings from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.settings.vue'
import CustomDataTableSettings from '@canvas-builder/components/ui/CustomDataTable/CustomDataTable.settings.vue'
import EmbeddableSettings from '@canvas-builder/components/ui/Embeddable/Embeddable.settings.vue'
import IFrameSettings from '@canvas-builder/components/ui/IFrame/IFrame.settings.vue'
import LinkSettings from '@canvas-builder/components/ui/Link/Link.settings.vue'
import MultimediaSettings from '@canvas-builder/components/ui/Multimedia/Multimedia.settings.vue'
import PageSettings from '@canvas-builder/components/ui/Page/Page.settings.vue'
import RootSettings from '@canvas-builder/components/ui/Root/Root.settings.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'

interface Props {
  baseStackingContext?: number
}

type DrawerComponentType =
  | typeof CarouselSettings
  | typeof EmbeddableSettings
  | typeof MultimediaSettings
  | typeof IFrameSettings
  | typeof RootSettings

const { t } = useI18n()
const { activeCanvas, activeEditNode, componentEditMode, sectionComponentOverrides, setComponentEditMode } = useCanvas()
const drawerComponentRef = ref<DrawerComponentType | null>(null)
const props = defineProps<Props>()

const drawerComponentsMap = {
  [ComponentTypes.Carousel]: CarouselSettings,
  [ComponentTypes.Embeddable]: EmbeddableSettings,
  [ComponentTypes.IFrame]: IFrameSettings,
  [ComponentTypes.Multimedia]: MultimediaSettings,
  [ComponentTypes.Page]: PageSettings,
} as Record<ComponentTypes, any>

const modalComponentsMap = {
  [ComponentTypes.Link]: LinkSettings,
  [ComponentTypes.DynamicDataTable]: DynamicDataTableSettings,
  [ComponentTypes.CustomDataTable]: CustomDataTableSettings,
  [ComponentTypes.DataCharts]: DataChartsSettings,
} as Record<ComponentTypes, any>

const onUpdateShow = (show: boolean) => {
  if (!show) setComponentEditMode(false)
}

const computedActiveEditNode = computed(() => {
  if (
    !componentEditMode.value ||
    !componentEditMode.value.usedInSectionId ||
    !sectionComponentOverrides.value[componentEditMode.value.usedInSectionId] ||
    !sectionComponentOverrides.value[componentEditMode.value.usedInSectionId][componentEditMode.value.id ?? '']
  ) {
    return { data: activeEditNode.value?.data, is_removed: false }
  }

  return {
    data: sectionComponentOverrides.value[componentEditMode.value.usedInSectionId][componentEditMode.value.id ?? '']
      .node?.data,
    is_removed:
      !!sectionComponentOverrides.value[componentEditMode.value.usedInSectionId][componentEditMode.value.id ?? '']
        .is_removed,
  }
})
const isDrawerComponent = computed(
  () => !!(componentEditMode.value && drawerComponentsMap[componentEditMode.value.type]),
)
const isModalComponent = computed(() => !!(componentEditMode.value && modalComponentsMap[componentEditMode.value.type]))
const type: ComputedRef<ComponentTypes> = computed(() =>
  componentEditMode.value ? componentEditMode.value.type : ComponentTypes.Root,
)
</script>
